import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-10 sm:py-12" data-testid="footer">
      <div className="container mx-auto px-4 sm:px-6 md:px-12">
        {/* Logo and Description - Full width on top */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="flex items-center justify-center gap-0.5 mb-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_live-listings-1/artifacts/kd4qptzr_Vitthal%20Watermark%20Image.png" 
              alt="AuctionGuru Logo" 
              className="h-7 w-7 sm:h-8 sm:w-8 object-contain"
            />
            <span className="font-heading font-bold text-lg sm:text-xl">
              <span className="text-white">Auction</span><span className="text-[#F5A623]">Guru</span>
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
            The UK's definitive property auction aggregator. Real-time listings from 40+ auction houses.
          </p>
        </div>

        {/* Links Grid - 2x2 on mobile, 4 columns on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-10">
          {/* Quick Links */}
          <div className="text-center">
            <h3 className="font-heading font-semibold mb-3 sm:mb-4 text-xs sm:text-sm text-white">Quick Links</h3>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <Link to="/properties" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Browse Properties
                </Link>
              </li>
              <li>
                <Link to="/calendar" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Auction Calendar
                </Link>
              </li>
              <li>
                <Link to="/auction-dates" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Auction Dates
                </Link>
              </li>
              <li>
                <Link to="/finance" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Finance
                </Link>
              </li>
            </ul>
          </div>

          {/* Finance Services */}
          <div className="text-center">
            <h3 className="font-heading font-semibold mb-3 sm:mb-4 text-xs sm:text-sm text-white">Finance Services</h3>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <Link to="/finance#bridging" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Bridging Loans
                </Link>
              </li>
              <li>
                <Link to="/finance#development" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Development Finance
                </Link>
              </li>
              <li>
                <Link to="/finance#commercial" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Commercial Mortgage
                </Link>
              </li>
              <li>
                <Link to="/finance#buy-to-let" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
                  Buy to Let Mortgage
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center">
            <h3 className="font-heading font-semibold mb-3 sm:mb-4 text-xs sm:text-sm text-white">Contact Us</h3>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <a href="mailto:Contact@AuctionGuru.co.uk" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
                  <Mail className="h-3.5 w-3.5 text-[#F5A623] flex-shrink-0" />
                  <span>Contact@AuctionGuru.co.uk</span>
                </a>
              </li>
              <li>
                <a href="tel:02034110999" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors inline-flex items-center justify-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-[#F5A623] flex-shrink-0" />
                  <span>02034110999</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Address */}
          <div className="text-center">
            <h3 className="font-heading font-semibold mb-3 sm:mb-4 text-xs sm:text-sm text-white">Address</h3>
            <div className="inline-flex items-start justify-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-[#F5A623] flex-shrink-0 mt-0.5" />
              <address className="text-xs sm:text-sm text-slate-400 not-italic text-center">
                Whispers, South Approach<br />
                Moor Park, HA6 2EU<br />
                United Kingdom
              </address>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer + Copyright */}
        <div className="border-t border-slate-800 pt-6 sm:pt-8 text-center space-y-4">
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-4">
            <Link to="/disclaimer" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
              Disclaimer
            </Link>
            <Link to="/terms" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
              Terms of Service
            </Link>
            <Link to="/listing-removal" className="text-xs sm:text-sm text-slate-400 hover:text-[#F5A623] transition-colors">
              Request Listing Removal
            </Link>
          </div>
          <p className="text-xs text-slate-500 max-w-2xl mx-auto leading-relaxed px-4">
            AuctionGuru aggregates publicly available auction listings and directs users to original auctioneer websites. 
            We do not conduct auctions. All property details should be verified with the respective auction house before bidding.
          </p>
          <p className="text-xs sm:text-sm text-slate-400">
            &copy; {new Date().getFullYear()} AuctionGuru. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
